import type Phaser from 'phaser';
import {
  checkLawn,
  MAX_CUSTOM_LAWNS,
  newCustomLawn,
  storeLawn,
  type CustomLawn,
} from '../../meta/customLawns';
import { decodeLawn, encodeLawn, findLawnCode } from '../../meta/lawnCode';
import { pasteText } from '../../platform/share';
import { saves } from '../records';
import { playCustomLawn } from './customPlay';

/**
 * Shared lawns (lm-ocm.7): a lawn code from a link or the clipboard, checked and played. Playing
 * one never needs the builder (owner): with the builder it is also kept in My Lawns.
 */

/** A code that came in with the page's link (?lawn=...), waiting for the menu to open it. */
let pendingCode: string | null = null;

/** Reads (and takes out of the address bar) a lawn code in the page's link, at boot. */
export function takeLinkedLawn(): void {
  try {
    const url = new URL(window.location.href);
    const code = url.searchParams.get('lawn');
    if (!code) return;
    pendingCode = code;
    url.searchParams.delete('lawn');
    window.history.replaceState(null, '', url.toString());
  } catch {
    // No usable location (a test harness): nothing linked.
  }
}

/** A code from a link that opened the app (universal links), for the menu to open. */
export function setPendingLawnCode(code: string): void {
  pendingCode = code;
}

/** The linked code, once (the menu opens it). */
export function pendingLawnCode(): string | null {
  const code = pendingCode;
  pendingCode = null;
  return code;
}

/**
 * Opens a lawn code: decoded, checked by the solver (never trusted), kept in My Lawns when the
 * builder is owned (once: the same lawn again is the same entry), then played. Returns what went
 * wrong, or null once its card is open.
 */
export function openLawnCode(scene: Phaser.Scene, code: string): string | null {
  const decoded = decodeLawn(code);
  if (!decoded.ok) return decoded.problem;
  const check = checkLawn(decoded.lawn);
  if (check.problems.length > 0) return "That lawn can't be cleared.";
  const save = saves().save;
  let lawn: CustomLawn = { ...decoded.lawn, par: check.par };
  let kept = false;
  if (save.builder.owned) {
    const same = save.customLawns.find((l) => encodeLawn(l) === encodeLawn(lawn));
    if (same) {
      lawn = { ...lawn, id: same.id };
      kept = true;
    } else if (save.customLawns.length < MAX_CUSTOM_LAWNS) {
      lawn = { ...lawn, id: newCustomLawn(save).id };
      kept = true;
    }
    if (kept) saves().update((s) => storeLawn(s, lawn));
  }
  if (!playCustomLawn(scene, lawn, { kept })) return 'None of your mowers can clear it.';
  return null;
}

/** PASTE: a lawn code (or a link with one) from the clipboard, opened; else what went wrong. */
export async function pasteLawn(scene: Phaser.Scene): Promise<string | null> {
  const text = await pasteText();
  const code = text ? findLawnCode(text) : null;
  if (!code) return 'No lawn code to paste.';
  return openLawnCode(scene, code);
}
